/**
 * 分身三条线的**路由单源**(2026-08-08 IA 收尾)。
 *
 * ★为什么要从 `features/digital-human` 提到 entities:入口分布在三个 feature
 *   (digital-human / video / explosive)+ 场景卡目录(entities/scene-card)。
 *   FSD 单向核心禁 features→features,所以 `VideoTaskDetailPage` 想直指 `/talking`
 *   只能自己抄一份字符串 —— 抄第二份的那天起,改路由就一定漏一处。
 *
 * ★这里只放**路由与线的对应**,不放线的业务语义(片型 / 计费 / 引擎),
 *   那些在 `features/digital-human/model` 各自的位置,不上提。
 */

export type DhLineKey = "talking" | "photo" | "tts";

export const DH_LINE_ROUTE: Record<DhLineKey, string> = {
  talking: "/ai-digital-human/talking",
  photo: "/ai-digital-human/photo",
  tts: "/ai-digital-human/tts",
};

/**
 * 预置音色 `usage` → **推荐**落哪条线(I5 声音库分流)。
 *
 * ★是「推荐」不是「判定」:选一个音色说明用户要这个**声音**,说明不了他要不要画面 ——
 *   三条线全都用音色。所以这里只给一个默认高亮,三条线仍全部列出来让用户自己点
 *   (设计稿 §3.4 原话:「问一句去哪条线(或按音色 usage 推荐)」)。
 *   直接按 usage 单跳会把「播报音色 + 想出镜」的用户扔到纯音频线上,他得再横跳一次。
 */
export const DH_VOICE_USAGE_LINE: Record<string, DhLineKey> = {
  broadcast: "tts", // 播报:门店 / 电话 / 广播,本来就不出画面
  cartoon: "tts", // 卡通:配音用途
  social: "talking", // 种草:短视频出镜口播
  explain: "talking", // 讲解:讲师出镜
  emotion: "talking", // 情感:有画面才有情绪落点
};

/**
 * 「走错门」换线时随身带走的东西 —— 目前只有文案(2026-08-12 测试反馈)。
 *
 * ★为什么另立一条而不是复用 recipe / dhRemake:那两条都是「别人替你填好的一稿」,
 *   recipe 四个字段全必填(塞半个会把形象预选成空)、dhRemake 认 fromTaskId(得先有任务)。
 *   换线发生在**用户自己正在填**的中途,手上只有文案,既没有任务也没有形象。
 *
 * ★它与 recipe / explosive / dhRemake 仍是**四选一**:同时生效会混出错配档案(存量踩过),
 *   所以落地端按既有优先级顺序判,这条排在最后。
 */
export interface DhLineSwitchHandoff {
  script: string;
}

export function parseDhLineSwitchState(state: unknown): DhLineSwitchHandoff | null {
  if (!state || typeof state !== "object") return null;
  const raw = (state as { dhLineSwitch?: unknown }).dhLineSwitch;
  if (!raw || typeof raw !== "object") return null;
  const script = (raw as Record<string, unknown>).script;
  if (typeof script !== "string" || !script.trim()) return null;
  return { script };
}
